<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
// NOTICE: KAPTION-INTEGRATION - upstream #15279: WhatsApp Flow responses
import WhatsappFlowResponseBubble from './bubbles/WhatsappFlowResponse.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: outgoing in-chat flow message
import KaptionWhatsappFlowBubble from './bubbles/KaptionWhatsappFlowBubble.vue';
// NOTICE: KAPTION-INTEGRATION — rumo-carousels.DASHBOARD.1: read-only carousel preview
import KaptionCardsBubble from './bubbles/KaptionCardsBubble.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.4
import KaptionCallBubble from 'dashboard/components-next/kaption/calls/KaptionCallBubble.vue';
import { isKaptionCallMessage } from 'dashboard/components-next/kaption/calls/kaptionCallModel';
import { carouselLayoutOf } from 'shared/helpers/KaptionCarousels';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION
// whatsapp-sent-template-tracking.RENDERING.4
import WhatsAppTemplateBubble from './bubbles/WhatsAppTemplate/Index.vue';

import MessageError from './MessageError.vue';
// NOTICE: KAPTION-INTEGRATION — note-cards.RENDER: structured note cards render as a pill
import KaptionNoteCardPill from './KaptionNoteCardPill.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
import KaptionMergedCaption from 'dashboard/components/kaption/KaptionMergedCaption.vue';
import { getStructuredNoteCard } from 'dashboard/helper/kaptionNoteCards';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  // NOTICE: KAPTION-INTEGRATION - Whether this message is grouped with the previous one (same sender, same minute)
  groupWithPrevious: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  isGroupConversation: { type: Boolean, default: false },
  // NOTICE: KAPTION-INTEGRATION - Track if message is currently being translated
  isMessageTranslating: { type: Boolean, default: false },
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();
// NOTICE: KAPTION-INTEGRATION — rumo-carousels.DASHBOARD.2
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Check for Kaption AI assistant messages first (copilot purple styling)
  // This must be checked before the generic private check to give AI messages their special styling
  // Note: props are transformed to camelCase by useCamelCase in MessageList.vue
  // feature-permissions.AI.6 - render-variant gate: fall back to default styling when permission is off
  const isKaptionAssistant =
    props.sender?.isKaptionAssistant &&
    canUseFeature('copilot_message_styling');
  if (isKaptionAssistant && props.messageType === MESSAGE_TYPES.OUTGOING) {
    // Return different variant for private vs non-private AI messages
    return props.private
      ? MESSAGE_VARIANTS.KAPTION_AI_PRIVATE
      : MESSAGE_VARIANTS.KAPTION_AI;
  }

  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  const isBot = !props.sender || props.sender.type === SENDER_TYPES.AGENT_BOT;
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

// NOTICE: KAPTION-INTEGRATION - Detect if this is a bot private note (activity note)
// Bot notes are identified by:
// 1. content_attributes.agent_note being true (new API-based approach)
// 2. Content starting with 🤖 emoji (legacy/existing messages)
// NOTICE: KAPTION-INTEGRATION — note-cards.RENDER.1: a private note carrying a structured card
// renders as the card, centered, without avatar or bubble. With the note_cards permission off the
// note keeps rendering as its plain text (note-cards.TEXT.2).
const structuredNoteCard = computed(() => {
  if (!props.private || !canUseFeature('note_cards')) return null;
  return getStructuredNoteCard(props.contentAttributes);
});

const isBotPrivateNote = computed(() => {
  if (!props.private) return false;
  // Check for API attribute first
  if (props.contentAttributes?.agentNote === true) return true;
  // Fallback to emoji detection for legacy messages
  const messageContent = props.content || '';
  return messageContent.trim().startsWith('🤖');
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

// NOTICE: KAPTION-INTEGRATION - Whether this message is grouped with the previous one
const shouldGroupWithPrevious = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithPrevious;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

// NOTICE: KAPTION-INTEGRATION - Check if sender is a Kaption AI assistant
const isKaptionAssistant = computed(() => props.sender?.isKaptionAssistant);

const componentToRender = computed(() => {
  // NOTICE: KAPTION-INTEGRATION
  // whatsapp-sent-template-tracking.RENDERING.4 — render the campaign template
  // bubble when the materialized Message carries a kaption_sent_template_id.
  if (
    props.messageType === MESSAGE_TYPES.TEMPLATE &&
    (props.contentAttributes?.kaptionSentTemplateId ||
      props.contentAttributes?.kaption_sent_template_id)
  ) {
    return WhatsAppTemplateBubble;
  }

  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  // NOTICE: KAPTION-INTEGRATION - upstream #15279: a completed WhatsApp Flow.
  if (props.contentAttributes?.whatsappFlowResponse) {
    return WhatsappFlowResponseBubble;
  }

  // NOTICE: KAPTION-INTEGRATION - whatsapp-flows: a flow we sent in the chat.
  if (props.contentAttributes?.kaptionWhatsappFlow) {
    return KaptionWhatsappFlowBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.4 / CALL_MODEL.3: a
    // Kaption call message (it carries kaption_call_id) renders the Kaption
    // bubble. Not flag-gated on purpose: turning the flag off must not make
    // call history unreadable (SCOPE.3 — never a silent gap).
    if (isKaptionCallMessage(props.contentType, props.contentAttributes)) {
      return KaptionCallBubble;
    }
    return VoiceCallBubble;
  }

  // NOTICE: KAPTION-INTEGRATION — rumo-carousels.DASHBOARD.1 / .2: v1 carousels
  // (cards with a layout) preview read-only; upstream cards render as before.
  if (
    carouselLayoutOf(props.contentType, props.contentAttributes) &&
    isKaptionFlagEnabled('KAPTION_RUMO_CAROUSELS')
  ) {
    return KaptionCardsBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  if (props.contentAttributes.imageType === 'story_mention') {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  const isSentByCurrentUser =
    (props.senderId ?? props.sender?.id) === props.currentUserId;

  return {
    copy: hasText,
    edit:
      isOutgoing &&
      props.private &&
      hasText &&
      !isMessageDeleted.value &&
      !isFailedOrProcessing &&
      isSentByCurrentUser,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    react: !isFailedOrProcessing && !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    // NOTICE: KAPTION-INTEGRATION - Allow reply-to for private notes on all channels
    replyTo:
      (props.private && !isFailedOrProcessing) ||
      (!props.private &&
        props.inboxSupportsReplyTo.outgoing &&
        !isFailedOrProcessing),
  };
});

const reactions = computed(() => {
  const allReactions = {};
  const ownReactions = props.contentAttributes?.reactions || {};
  // NOTICE: KAPTION-INTEGRATION - MessageList.vue uses useCamelCase with deep:true
  // Check camelCase first (from MessageList), then fallback to snake_case for backward compatibility
  const externalReactions =
    props.contentAttributes?.externalReactions ||
    props.contentAttributes?.external_reactions ||
    {};

  Object.values(ownReactions).forEach(emoji => {
    allReactions[emoji] = (allReactions[emoji] || 0) + 1;
  });

  Object.values(externalReactions).forEach(emoji => {
    allReactions[emoji] = (allReactions[emoji] || 0) + 1;
  });

  return Object.entries(allReactions).map(([emoji, count]) => ({
    emoji,
    count,
  }));
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  // NOTICE: KAPTION-INTEGRATION - upstream #15279: render flow responses even
  // when the message has no text content.
  const hasWhatsappFlowResponse =
    !!props.contentAttributes?.whatsappFlowResponse;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    hasWhatsappFlowResponse ||
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.4: the call message has
    // no text content; without this the KaptionCallBubble never renders.
    isKaptionCallMessage(props.contentType, props.contentAttributes)
  );
});

// NOTICE: KAPTION-INTEGRATION - Context menu opening handler
function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

function handleEdit() {
  emitter.emit(BUS_EVENTS.TOGGLE_EDIT_MESSAGE, {
    id: props.id,
    content: props.content,
    conversationId: props.conversationId,
  });
}

const avatarInfo = computed(() => {
  // If no sender, return bot info
  if (!props.sender) {
    return {
      name: t('CONVERSATION.BOT'),
      src: '',
    };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  isGroupConversation: computed(() => props.isGroupConversation),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
  // NOTICE: KAPTION-INTEGRATION - Provide groupWithPrevious to suppress sender name on consecutive messages
  shouldGroupWithPrevious,
  // NOTICE: KAPTION-INTEGRATION - Provide isMessageTranslating to child components
  isMessageTranslating: computed(() => props.isMessageTranslating),
  // NOTICE: KAPTION-INTEGRATION - Provide isBotPrivateNote to child components
  isBotPrivateNote,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full message-bubble-container mb-2"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION — note-cards.RENDER -->
    <div v-else-if="structuredNoteCard" class="flex w-full justify-center px-2">
      <KaptionNoteCardPill :note-card="structuredNoteCard" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL || isBotPrivateNote,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <!-- NOTICE: KAPTION-INTEGRATION - Avatar: copilot icon for AI assistants (except bot activity notes), regular avatar for others -->
      <div
        v-if="
          ((isKaptionAssistant && !isBotPrivateNote) || !shouldGroupWithNext) &&
          shouldShowAvatar
        "
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <!-- Kaption AI assistant: show copilot sparkles icon (not for bot activity notes) -->
        <div
          v-if="isKaptionAssistant && !isBotPrivateNote"
          class="flex items-center justify-center w-6 h-6 rounded-full bg-kpt-copilot shadow-[0_0_12px_rgba(124,58,237,0.33)]"
        >
          <span class="i-woot-copilot-sparkles text-white text-sm" />
        </div>
        <!-- Regular user/agent avatar -->
        <Avatar v-else v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex flex-col"
        :class="{
          'items-end': orientation === ORIENTATION.RIGHT && !isBotPrivateNote,
          'items-start': orientation === ORIENTATION.LEFT && !isBotPrivateNote,
          'items-center': isBotPrivateNote,
          'min-w-0':
            variant === MESSAGE_VARIANTS.EMAIL ||
            componentToRender === KaptionCardsBubble,
          'w-full': isBotPrivateNote,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - rumo-carousels: the bubble column can shrink (min-w-0) so a wide card row scrolls inside its bubble instead of overflowing -->
        <div
          class="flex"
          :class="{
            'min-w-0': componentToRender === KaptionCardsBubble,
            'ltr:ml-8 rtl:mr-8 justify-end':
              orientation === ORIENTATION.RIGHT && !isBotPrivateNote,
            'ltr:mr-8 rtl:ml-8':
              orientation === ORIENTATION.LEFT && !isBotPrivateNote,
            'justify-center': isBotPrivateNote,
            'w-full': isBotPrivateNote,
          }"
        >
          <Component :is="componentToRender" />
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging -->
        <KaptionMergedCaption
          v-if="contentAttributes?.kaptionMerge"
          :merge="contentAttributes.kaptionMerge"
        />

        <div
          v-if="reactions.length > 0"
          class="flex flex-wrap gap-1 mt-[-10px] relative z-10"
          :class="{
            'ltr:ml-8 rtl:mr-8':
              orientation === ORIENTATION.RIGHT && !isBotPrivateNote,
            'ltr:mr-8 rtl:ml-8':
              orientation === ORIENTATION.LEFT && !isBotPrivateNote,
          }"
        >
          <span
            v-for="reaction in reactions"
            :key="reaction.emoji"
            class="px-1.5 py-0.5 rounded-full text-xs shadow-sm border border-n-strong/10 bg-white dark:bg-slate-700 dark:border-slate-600"
          >
            {{ reaction.emoji }}
            <span v-if="reaction.count > 1" class="text-xs font-medium ml-0.5">
              {{ reaction.count }}
            </span>
          </span>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @edit="handleEdit"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}
</style>
